import { useEffect, useMemo, useState } from 'react';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import { DeviceLocationVia, type Tenant } from '@etiquetachef/types';
import { TenantMapEmbed } from './TenantMapEmbed';
import { formatDateTime } from '@/lib/format';
import {
  distanceKm,
  subscribeOperatingLocations,
  type OperatingLocationRow,
  type OperatingLocationsResult,
} from '@/lib/firestoreOperatingLocations';

/**
 * Distância do endereço declarado a partir da qual a célula é destacada. 5 km
 * não é medido: é maior que a imprecisão da posição aproximada (~3 km, aceita
 * pelo owner na #996) e menor que a distância entre bairros de uma cidade.
 */
export const FAR_FROM_ADDRESS_KM = 5;

type Props = { tenant: Tenant };

/**
 * Seção "Onde opera" da aba Local: as células onde o aparelho do tenant
 * imprimiu etiquetas ou gravou tags NFC, comparadas ao endereço declarado.
 *
 * Origem: #996 — 2026-10-18 14:17 +0200 (owner: a equipe precisa saber onde
 * exatamente cada tenant opera).
 *
 * Por quê: o endereço do tenant é o DECLARADO no onboarding; a célula é o
 * OBSERVADO. A distância entre os dois (destaque acima de
 * `FAR_FROM_ADDRESS_KM`) é o que a equipe procura.
 *
 * Contrato: escuta `subscribeOperatingLocations`; clicar numa linha centraliza
 * o mapa naquela célula. Sem células, mostra o vazio explicando que exige a
 * versão do app com a gravação da posição (builds antigos não mandam).
 *
 * Usado por: `TenantLocationTab` (aba Local do `routes/TenantDetailPage.tsx`).
 *
 * Arquitetura: leitura só de super-admin (rules); o dado é escrito pelo trigger
 * `onLabelWritten`. Nada aqui escreve.
 *
 * Dicas: o mapa reaproveita o `TenantMapEmbed` com um tenant sintético (sem
 * `placeId`, com as coordenadas da célula) — o embed escolhe `placeId` antes
 * das coordenadas, então o `placeId` precisa ir `null`.
 */
export function OperatingLocationsSection({ tenant }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [result, setResult] = useState<OperatingLocationsResult>({ kind: 'LOADING' });
  const [selected, setSelected] = useState<OperatingLocationRow | null>(null);

  useEffect(() => {
    setResult({ kind: 'LOADING' });
    setSelected(null);
    return subscribeOperatingLocations(tenant.id, setResult);
  }, [tenant.id]);

  const declared =
    typeof tenant.latitude === 'number' && typeof tenant.longitude === 'number'
      ? { lat: tenant.latitude, lng: tenant.longitude }
      : null;

  const rows = result.kind === 'READY' ? result.rows : [];
  const distanceOf = (r: OperatingLocationRow): number | null =>
    declared === null ? null : distanceKm(declared.lat, declared.lng, r.lat, r.lng);

  const mapTenant = useMemo<Tenant | null>(
    () => (selected === null ? null : { ...tenant, placeId: null, latitude: selected.lat, longitude: selected.lng }),
    [selected, tenant],
  );

  const when = (ms: number | null): string => (ms === null ? '—' : formatDateTime(ms, i18n.language));

  return (
    <SpaceBetween size="m">
      {result.kind === 'ERROR' ? (
        <Alert type="error">{t('tenantDetail.location.operating.error')}</Alert>
      ) : null}
      <Table<OperatingLocationRow>
        header={
          <Header
            variant="h3"
            counter={result.kind === 'READY' ? `(${String(rows.length)})` : undefined}
            description={t('tenantDetail.location.operating.description')}
          >
            {t('tenantDetail.location.operating.title')}
          </Header>
        }
        loading={result.kind === 'LOADING'}
        items={rows}
        trackBy="geohash"
        selectionType="single"
        selectedItems={selected ? [selected] : []}
        onSelectionChange={({ detail }) => {
          setSelected(detail.selectedItems[0] ?? null);
        }}
        empty={<Box textAlign="center">{t('tenantDetail.location.operating.empty')}</Box>}
        columnDefinitions={[
          { id: 'cell', header: t('tenantDetail.location.operating.colCell'), cell: (r) => r.geohash },
          {
            id: 'coords',
            header: t('tenantDetail.location.operating.colCoords'),
            cell: (r) => `${r.lat.toFixed(5)}, ${r.lng.toFixed(5)}`,
          },
          {
            id: 'accuracy',
            header: t('tenantDetail.location.operating.colAccuracy'),
            cell: (r) => `${Math.round(r.bestAccuracyM).toString()} m`,
          },
          {
            id: 'distance',
            header: t('tenantDetail.location.operating.colDistance'),
            cell: (r): React.ReactNode => {
              const km = distanceOf(r);
              if (km === null) return '—';
              const label = `${km.toFixed(1)} km`;
              return km > FAR_FROM_ADDRESS_KM ? (
                <StatusIndicator type="warning">
                  {label} · {t('tenantDetail.location.operating.far')}
                </StatusIndicator>
              ) : (
                label
              );
            },
          },
          { id: 'sessions', header: t('tenantDetail.location.operating.colSessions'), cell: (r) => r.sessions },
          {
            id: 'via',
            header: t('tenantDetail.location.operating.colVia'),
            cell: (r) =>
              `${
                r.lastVia === DeviceLocationVia.NFC
                  ? t('tenantDetail.location.operating.viaNfc')
                  : t('tenantDetail.location.operating.viaPrint')
              } · ${r.lastPlatform}`,
          },
          {
            id: 'firstSeen',
            header: t('tenantDetail.location.operating.colFirstSeen'),
            cell: (r) => when(r.firstSeenAtMillis),
          },
          {
            id: 'lastSeen',
            header: t('tenantDetail.location.operating.colLastSeen'),
            cell: (r) => when(r.lastSeenAtMillis),
          },
        ]}
      />
      {mapTenant ? (
        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">{t('tenantDetail.location.operating.mapOfCell')}</Box>
          <TenantMapEmbed tenant={mapTenant} />
        </SpaceBetween>
      ) : null}
    </SpaceBetween>
  );
}
